import * as checks from './checks'
import {MissingRefError} from './errors'
import * as util from './util'

const VALIDATORS_MAPPING = {
  const: 'value',
  enum: 'values',
  maxItems: 'maxLength',
  minItems: 'minLength',
}

/**
 * Validate JSON instance using given JSON schema
 * @param {Array.<string>} absoluteInstancePath
 * @param {Array.<string>} absoluteSchemaPath
 * @param {*} instance
 * @param {Object.<string, *>} schema
 * @param {function(ValidationError): undefined} onError
 * @returns {undefined}
 */
function validate(
  absoluteInstancePath,
  absoluteSchemaPath,
  instance,
  schema,
  onError
) {
  function createError(validator, validatorValue, context) {
    return {
      absoluteInstancePath,
      absoluteSchemaPath: absoluteSchemaPath.concat([validator]),
      context,
      validator,
      validatorValue,
    }
  }

  if (schema == null) {
    return null
  }

  const schemaSlice = util.get(undefined, absoluteSchemaPath, schema)

  if (schemaSlice == null) {
    return null
  }

  if (typeof schemaSlice.$ref === 'string') {
    const absoluteRefPath = schemaSlice.$ref.split('/').slice(1)

    util.get(
      new MissingRefError(`"${schemaSlice.$ref}" is missing in root schema`),
      absoluteRefPath,
      schema
    )

    return validate(
      absoluteInstancePath,
      absoluteRefPath,
      instance,
      schema,
      onError
    )
  }

  const instanceSlice = util.get(undefined, absoluteInstancePath, instance)

  for (const validator in schemaSlice) {
    const validatorValue = schemaSlice[validator]
    switch (validator) {
      case 'additionalItems': {
        if (Array.isArray(schemaSlice.items)) {
          const start = schemaSlice.items.length
          const end = instanceSlice.length
          for (let index = start; index < end; index++) {
            validate(
              absoluteInstancePath.concat([index]),
              absoluteSchemaPath.concat([validator, index]),
              instance,
              schema,
              onError
            )
          }
        }
        break
      }
      case 'additionalProperties': {
        const instanceProperties = Object.keys(instanceSlice)
        const properties = util.get({}, ['properties'], schemaSlice)
        const patterns = util.map(
          pattern => new RegExp(pattern),
          Object.keys(util.get({}, ['patternProperties'], schemaSlice))
        )

        for (const key of instanceProperties) {
          if (
            !properties.hasOwnProperty(key) &&
            !util.any(re => re.test(key), patterns)
          ) {
            validate(
              absoluteInstancePath.concat([key]),
              absoluteSchemaPath.concat([validator]),
              instance,
              schema,
              onError
            )
          }
        }
        break
      }
      case 'allOf':
      case 'anyOf':
      case 'oneOf': {
        if (Array.isArray(validatorValue)) {
          const validations = []
          for (const index in validatorValue) {
            const errors = []
            validate(
              absoluteInstancePath,
              absoluteSchemaPath.concat([validator, index]),
              instance,
              schema,
              error => errors.push(error)
            )
            validations.push(errors)
          }
          const failedCount = util.reject(util.isEmptyArray, validations).length
          const passedCount = validatorValue.length - failedCount

          if (
            (validator === 'allOf' && failedCount > 0) ||
            (validator === 'anyOf' && passedCount < 1) ||
            (validator === 'oneOf' && passedCount !== 1)
          ) {
            onError(
              createError(validator, validatorValue, util.unnest(validations))
            )
          }
        }

        break
      }
      case 'contains': {
        const context = []

        for (const index in instanceSlice) {
          const errors = []

          validate(
            absoluteInstancePath.concat([index]),
            absoluteSchemaPath.concat([validator]),
            instance,
            schema,
            error => {
              context.push(error)
              errors.push(error)
            }
          )

          // Stop and succeed if any instance item passes validation
          if (errors.length === 0) {
            context.length = 0
            break
          }
        }

        if (instanceSlice.length === 0 || context.length) {
          onError(createError(validator, validatorValue, context))
        }

        break
      }
      case 'dependencies': {
        for (const dependency in validatorValue) {
          if (instanceSlice.hasOwnProperty(dependency)) {
            if (Array.isArray(validatorValue[dependency])) {
              for (const property of validatorValue[dependency]) {
                if (!instanceSlice.hasOwnProperty(property)) {
                  onError(createError(validator, validatorValue))
                }
              }
            } else {
              validate(
                absoluteInstancePath,
                absoluteSchemaPath.concat([validator, dependency]),
                instance,
                schema,
                onError
              )
            }
          }
        }
        break
      }
      case 'if': {
        let valid = true
        validate(
          absoluteInstancePath,
          absoluteSchemaPath.concat(['if']),
          instance,
          schema,
          () => {
            valid = false
          }
        )

        const conditionalValidator = valid ? 'then' : 'else'

        validate(
          absoluteInstancePath,
          absoluteSchemaPath.concat([conditionalValidator]),
          instance,
          schema,
          onError
        )

        break
      }
      case 'items': {
        if (Array.isArray(schemaSlice.items)) {
          for (const index in validatorValue) {
            validate(
              absoluteInstancePath.concat([index]),
              absoluteSchemaPath.concat([validator, index]),
              instance,
              schema,
              onError
            )
          }
        } else {
          for (const index in instanceSlice) {
            validate(
              absoluteInstancePath.concat([index]),
              absoluteSchemaPath.concat([validator]),
              instance,
              schema,
              onError
            )
          }
        }

        break
      }
      case 'not': {
        const {valid} = validateSchema(validatorValue, instanceSlice)
        if (valid) {
          onError(createError(validator, validatorValue))
        }
        break
      }
      case 'patternProperties': {
        for (const pattern in validatorValue) {
          const re = new RegExp(pattern)
          const properties = Object.keys(instanceSlice).filter(key =>
            re.test(key)
          )
          for (const key of properties) {
            validate(
              absoluteInstancePath.concat([key]),
              absoluteSchemaPath.concat([validator, pattern]),
              instance,
              schema,
              onError
            )
          }
        }

        break
      }
      case 'properties': {
        for (const key in instanceSlice) {
          validate(
            absoluteInstancePath.concat([key]),
            absoluteSchemaPath.concat([validator, key]),
            instance,
            schema,
            onError
          )
        }

        break
      }
      case 'required': {
        const context = []
        for (const key of validatorValue) {
          if (instanceSlice == null || !instanceSlice.hasOwnProperty(key)) {
            context.push({
              absoluteInstancePath: absoluteInstancePath.concat([key]),
              absoluteSchemaPath: absoluteSchemaPath.concat([validator]),
              validator,
              validatorValue,
            })
          }
        }

        if (context.length > 0) {
          onError(createError(validator, validatorValue, context))
        }

        break
      }
      default: {
        const check = VALIDATORS_MAPPING[validator] || validator
        const fn = checks[check]

        if (fn && !fn(validatorValue, instanceSlice)) {
          onError(createError(validator, validatorValue))
        }

        break
      }
    }
  }
}

export default function validateSchema(schema, instance) {
  const errors = []

  validate([], [], instance, schema, error => errors.push(error))

  return {
    valid: errors.length === 0,
    errors,
  }
}
